import { zodResolver } from '@hookform/resolvers/zod';
import type { DefaultValues, Resolver } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Accordion } from '~/components/Accordion';
import { ColumnLayout } from '~/components/ColumnLayout';
import { type FormProps } from '~/components/Form';
import { Form } from '~/components/Form';
import { FormFieldInput, FormFieldToggle } from '~/components/FormField';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { SelectOrganization } from '~settings/components/SelectOrganization';
import { SelectUserType } from '~settings/components/SelectUserType';

import { useOktaUserData } from './hooks/useOktaUserData';
import { ExternalPermissions } from './parts/ExternalPermissions';
import { InternalPermissions } from './parts/InternalPermissions';
import { RoleToResources } from './parts/RoleToResources';
import { type UserFormSchema, userFormSchema } from './schema';

type UserFormProps = Omit<FormProps<UserFormSchema>, 'formHook' | 'schema'>;

export function UserForm({
  onSubmit,
  values,
  onError,
  ...formProps
}: UserFormProps) {
  const { t } = useTranslation('settings', { keyPrefix: 'user' });
  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);
  const isExternal = useIsExternalUser();

  const form = useForm<UserFormSchema>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isActive: true,
      jobTitle: '',
      userType: values?.userType ?? 'EXTERNAL',
      ...(isInternalUserUpdatesEnabled
        ? {
            roleMap: [
              {
                role: '',
                resources: [],
              },
            ],
          }
        : {
            internalPermissions: undefined,

            // External permissions must be present for both internal and external
            externalPermissions: [
              {
                role: undefined,
                labels: [],
              },
            ],
          }),
    } as DefaultValues<UserFormSchema>,
    values,
    resolver: zodResolver(userFormSchema, {
      error: getI18nErrorMap((s) => t(`validations.${s}`)),
    }) as Resolver<UserFormSchema>,
    mode: 'onBlur',
  });

  const { watch, reset, getValues } = form;

  const isEditMode = !!values;

  const { readOnlyFields } = useOktaUserData(form, !isEditMode);

  const selectedUserType = isEditMode
    ? values?.userType
    : // Path gets broken with TExternal generic
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      watch('userType' as any);
  const selectedTypeIsExternal =
    selectedUserType === 'EXTERNAL' || selectedUserType === 'external';

  const handleUserTypeChange = () => {
    if (isInternalUserUpdatesEnabled && isEditMode) {
      return;
    }

    // @ts-expect-error Setting role and resources to empty values breaks the schema, but the default values shouldn't be valid
    reset({
      ...getValues(),
      organization: '',
      ...(isInternalUserUpdatesEnabled
        ? {
            roleMap: [
              {
                role: '',
                resources: [],
              },
            ],
          }
        : {
            internalPermissions: {
              roles: [],
              labelGroups: [],
            },
            externalPermissions: [
              {
                role: '',
                labels: [],
              },
            ],
          }),
    });
  };

  return (
    <Form
      aria-label={t('formDescription')}
      formHook={form}
      onError={onError}
      onSubmit={onSubmit}
      schema={userFormSchema}
      {...formProps}
    >
      <Accordion defaultValue={['basicInfo', 'userInfo', 'userPermissions']}>
        <Accordion.Item value="basicInfo" title={t('basicInformationTitle')}>
          <ColumnLayout>
            <FormFieldInput
              name="firstName"
              label={t('fields.firstName')}
              placeholder={t('placeholders.firstName')}
              readOnly={isEditMode || readOnlyFields.includes('firstName')}
            />
            <FormFieldInput
              name="lastName"
              label={t('fields.lastName')}
              placeholder={t('placeholders.lastName')}
              readOnly={isEditMode || readOnlyFields.includes('lastName')}
            />
            <FormFieldInput
              name="email"
              type="email"
              label={t('fields.email')}
              placeholder={t('placeholders.email')}
              readOnly={isEditMode}
            />
            <FormFieldInput
              name="phone"
              type="tel"
              label={t('fields.phone')}
              placeholder={t('placeholders.phone')}
              readOnly={readOnlyFields.includes('phone')}
            />
            <FormFieldToggle
              name="isActive"
              label={t('fields.status')}
              inactiveLabel={t('statusDisabled')}
              activeLabel={t('statusActive')}
            />
            {isExternal ? (
              <FormFieldInput
                name="jobTitle"
                label={t('fields.jobTitle')}
                placeholder={t('placeholders.jobTitle')}
              />
            ) : null}
          </ColumnLayout>
        </Accordion.Item>
        {!isExternal && (
          <Accordion.Item value="userInfo" title={t('userInformationTitle')}>
            <ColumnLayout>
              <FormFieldInput
                name="jobTitle"
                label={t('fields.jobTitle')}
                placeholder={t('placeholders.jobTitle')}
              />
              <SelectUserType
                name="userType"
                onChange={handleUserTypeChange}
                readOnly={
                  !isInternalUserUpdatesEnabled ||
                  (isEditMode && selectedTypeIsExternal)
                }
                targetUserType={isEditMode ? selectedUserType : undefined}
              />
              {!selectedTypeIsExternal ? (
                <SelectOrganization name="organization" />
              ) : null}
            </ColumnLayout>
          </Accordion.Item>
        )}
        <Accordion.Item value="userPermissions" title={t('permissionsTitle')}>
          {isInternalUserUpdatesEnabled ? (
            <>
              <p className="mb-md text-subtle">{t('roleLabelNotice')}</p>
              <RoleToResources name="roleMap" isEditing={isEditMode} />
            </>
          ) : (
            <div className="flex flex-col gap-md">
              <p className="text-subtle">{t('roleLabelNotice')}</p>
              {!selectedTypeIsExternal ? (
                <InternalPermissions name="internalPermissions" />
              ) : (
                <ExternalPermissions
                  name="externalPermissions"
                  isEditing={isEditMode}
                />
              )}
            </div>
          )}
        </Accordion.Item>
      </Accordion>
    </Form>
  );
}

UserForm.displayName = 'UserForm';
